import { MyDeal } from 'state/contexts/DealsContext/DealsContext.types';
import { Social } from 'types/social';
import { ApiStorageProvider } from 'types/storageProvider';
import { Merge } from './common';

export enum ServerSignatureType {
  Signature = 0,
  Ledger = 1,
}

export enum UserWalletType {
  Signature = 'Signature',
  Ledger = 'Ledger',
}

export type Address = {
  IDAddress: string;
  pubKeyAddress: string | null;
  actorType: string;
  isVerified: boolean;
};

export type UserAddress = Address & {
  signatureType: ServerSignatureType;
  storageProvider: ApiStorageProvider | null;
};

export type MultisigAddress = Merge<
  Omit<Address, 'pubKeyAddress'>,
  { pubKeyAddress: null }
>;

export type UploadedFileStatus = 'FOR_UPLOAD' | 'PINNED_TO_IPFS' | 'STORED_ON_FILECOIN';

export type UploadedFile = {
  name: string;
  cid: string;
  size: number;
  createdAt: string;
  deals: MyDeal[];
  status: UploadedFileStatus;
};

export type ApiUser = {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  company: string;
  avatarCode: string;
  email: string;
  social: Social;
  addresses: UserAddress[];
  uploadedFiles: UploadedFile[];
};

export enum AllowanceWizardStep {
  SetPayoutAddress,
  MakeDeposit,
  ConnectFilmineAddress,
  ConversionFinished,
}

export type AllowanceProgress = 'Pending' | 'InProgress' | 'Done';

export type DespositAddress = Omit<Address, 'IDAddress' | 'pubKeyAddress'> & {
  IDAddress: string | null;
  pubKeyAddress: string;
};

export type Payment = {
  amount: string;
  createdAt: string;
  id: string;
  messageCid: string;
  updatedAt: string;
};

export type AllowanceStatus =
  | 'PENDING_DEPOSIT'
  | 'PENDING_MULTISIG_CREATION'
  | 'PENDING_OWNER_ADDRESS_CHANGE_APPROVAL'
  | 'PENDING_OWNER_ADDRESS_CHANGE'
  | 'ACTIVE'
  | 'EXPIRED';

type BaseAllowance = {
  id: string;
  status: AllowanceStatus;
  minerId: string;
  coSignerAddress: Address;
  rewardAddress: Address;
  depositAddress: DespositAddress;
  createdAt: string;
  updatedAt: string;
};

type NoFieldOverlap<A, B> = keyof A & keyof B extends never ? true : false;

export type AllowanceInstance<
  Status extends AllowanceStatus,
  AdditionalAllowanceFields extends object = {},
> = NoFieldOverlap<BaseAllowance, AdditionalAllowanceFields> extends true
  ? Merge<AdditionalAllowanceFields, Merge<BaseAllowance, { status: Status }>>
  : never;

export type AllowancePendingDeposit = AllowanceInstance<'PENDING_DEPOSIT'>;

export type AllowancePendingMsigCreation = AllowanceInstance<'PENDING_MULTISIG_CREATION'>;

export type AllowancePendingOwnerChangeApproval = AllowanceInstance<
  'PENDING_OWNER_ADDRESS_CHANGE_APPROVAL',
  { multisigAddress: MultisigAddress }
>;

export type AllowancePendingOwnerChange = AllowanceInstance<
  'PENDING_OWNER_ADDRESS_CHANGE',
  { multisigAddress: MultisigAddress }
>;

type RealizedAllowanceData = {
  multisigAddress: MultisigAddress;
  depositedAmount: string;
  allowanceAmount: string;
  totalAllowancesReleased: string;
  totalRewardsPaidOut: string;
  startedAt: string;
  expiresAt: string;
  pledgedAmount: string;
};
export type AllowanceActive = AllowanceInstance<'ACTIVE', RealizedAllowanceData>;

export type AllowanceExpired = AllowanceInstance<'EXPIRED', RealizedAllowanceData>;

export type Allowance =
  | AllowancePendingDeposit
  | AllowancePendingMsigCreation
  | AllowancePendingOwnerChangeApproval
  | AllowancePendingOwnerChange
  | AllowanceActive
  | AllowanceExpired;

export type UserAllowances = { [minerId: string]: Allowance };

export type User = ApiUser & {
  allowances: UserAllowances;
};

export type SignData = {
  address: string;
  serializedMessage: string;
  signature: string;
};

export type RewardsPayout = {
  amount: string;
  createdAt: string;
  messageCid: string;
  minerId: string;
  allowanceId: string;
};

export type RewardsPayoutPayload = {
  count: number;
  limit: number;
  offset: number;
  results: RewardsPayout[];
};

export type FilgramFilePayload = Omit<UploadedFile, 'deals' | 'createdAt' | 'status'>;
